import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Papa from "papaparse";
import { toast } from "react-hot-toast";
import { Table, Modal, Button as BootstrapButton } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { FeePresetService } from "../../services/feePresetService";
import { AllClasses } from "../../configs/allClasses";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

const getClassName = (code) => {
  const found = AllClasses.find((c) => c.code === code);
  return found ? found.name : code;
};

export function FeesSetup() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auto-preset term and session
  const { currentSession, currentTerm } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  // Form state
  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");
  const [classId, setClassId] = useState(AllClasses[0]?.code || "");
  const [tuitionAmount, setTuitionAmount] = useState("");
  const [examAmount, setExamAmount] = useState("");

  // Modals state
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFromSession, setCopyFromSession] = useState("");
  const [copyFromTerm, setCopyFromTerm] = useState("");

  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setCopyFromSession(currentSession);
      setCopyFromTerm(currentTerm);
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      fetchPresets();
    }
  }, [session, term, isInitialized]);

  const fetchPresets = async () => {
    try {
      const res = await FeePresetService.getPresets({ session, term });
      setPresets(res.data);
    } catch (error) {
      console.error("Error fetching presets", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const tAmount = Number(tuitionAmount);
    const eAmount = Number(examAmount);

    if (tAmount < 0 || eAmount < 0) {
      toast.error("Amounts cannot be negative");
      setLoading(false);
      return;
    }

    try {
      await FeePresetService.createPreset({
        classId,
        session,
        term,
        tuitionAmount: tAmount,
        examFeeAmount: eAmount,
      });

      toast.success("Fee preset saved successfully!");
      setTuitionAmount("");
      setExamAmount("");
      fetchPresets();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save preset");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this preset?")) return;
    try {
      await FeePresetService.deletePreset(id);
      toast.success("Preset deleted");
      fetchPresets();
    } catch (error) {
      toast.error("Failed to delete preset");
    }
  };

  const handleDownloadTemplate = () => {
    const validClasses = AllClasses.filter((c) => c.code !== "GRADUATED").map(
      (c) => c.code
    );
    const csvData = validClasses.map((code) => ({
      ClassCode: code,
      TuitionAmount: "",
      ExamAmount: "",
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Fee_Presets_Template_${session.replace("/", "-")}_${term}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        const validPresets = [];

        rows.forEach((row) => {
          const cCode = row.ClassCode?.trim();
          const tAmount = Number(row.TuitionAmount);
          const eAmount = Number(row.ExamAmount);

          if (cCode && tAmount >= 0 && eAmount >= 0) {
            validPresets.push({
              classId: cCode,
              session,
              term,
              tuitionAmount: tAmount,
              examFeeAmount: eAmount,
            });
          }
        });

        if (validPresets.length === 0) {
          toast.error("No valid presets found in CSV");
          return;
        }

        setLoading(true);
        try {
          await FeePresetService.bulkUploadCsv({ presets: validPresets });
          toast.success(`Successfully uploaded ${validPresets.length} presets!`);
          fetchPresets();
        } catch (error) {
          toast.error(error?.response?.data?.message || "Failed to upload CSV");
        } finally {
          setLoading(false);
        }
      },
    });
    // clear input
    e.target.value = null;
  };

  const handleCopyFromPrevious = async () => {
    setLoading(true);
    try {
      await FeePresetService.copyFromPrevious({
        fromSession: copyFromSession,
        fromTerm: copyFromTerm,
        toSession: session,
        toTerm: term,
      });
      toast.success("Successfully copied presets!");
      setShowCopyModal(false);
      fetchPresets();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to copy presets");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <h3>Fees Setup & Presets</h3>
        <p>Set the mandatory Tuition and Exam fees for each class per term.</p>
      </Header>

      <Controls>
        <FormGroup>
          <label>Academic Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="2023/2024">2023/2024</option>
            <option value="2024/2025">2024/2025</option>
            <option value="2025/2026">2025/2026</option>
            <option value="2026/2027">2026/2027</option>
            <option value="2027/2028">2027/2028</option>
            <option value="2028/2029">2028/2029</option>

          </select>
        </FormGroup>

        <FormGroup>
          <label>Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            <option value="FIRST TERM">FIRST TERM</option>
            <option value="SECOND TERM">SECOND TERM</option>
            <option value="THIRD TERM">THIRD TERM</option>
          </select>
        </FormGroup>

        <div style={{ marginLeft: "auto", display: "flex", gap: "10px", alignItems: "center", alignSelf: "flex-end" }}>
          {/* <ActionButton
            type="button"
            onClick={() => setShowCopyModal(true)}
            style={{ backgroundColor: "#ff9800" }}
          >
            <Icon icon="mdi:content-copy" width="18" style={{ marginRight: 5 }} />
            Copy from Previous Term
          </ActionButton> */}

          <ActionButton type="button" onClick={handleDownloadTemplate} style={{ backgroundColor: "#28a745" }}>
            <Icon icon="mdi:download" width="18" style={{ marginRight: 5 }} />
            Template
          </ActionButton>

          <ActionButton
            as="label"
            htmlFor="csv-upload"
            style={{ backgroundColor: "#17a2b8", marginBottom: 0 }}
          >
            <Icon icon="mdi:upload" width="18" style={{ marginRight: 5 }} />
            Upload CSV
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              style={{ display: "none" }}
            />
          </ActionButton>
        </div>
      </Controls>

      <ContentGrid>
        <Card>
          <h4>Create / Update Preset</h4>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <label>Class</label>
              <select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                required
              >
                {AllClasses.filter(c => c.code !== "GRADUATED").map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <label>Tuition Fee Amount (₦)</label>
              <input
                type="number"
                min="0"
                value={tuitionAmount}
                onChange={(e) => setTuitionAmount(e.target.value)}
                required
                placeholder="e.g 85000"
              />
            </FormGroup>

            <FormGroup>
              <label>Exam/Utility Fee (₦)</label>
              <input
                type="number"
                min="0"
                value={examAmount}
                onChange={(e) => setExamAmount(e.target.value)}
                required
                placeholder="e.g 5000"
              />
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Preset"}
            </Button>
          </Form>
        </Card>

        <Card>
          <h4>
            Current Presets for {session} {term}
          </h4>
          {presets.length === 0 ? (
            <EmptyState>No presets found for this term yet.</EmptyState>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Tuition (₦)</th>
                  <th>Exam (₦)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {presets.map((p) => (
                  <tr key={p._id}>
                    <td>{getClassName(p.classId)}</td>
                    <td>{p.tuitionAmount.toLocaleString()}</td>
                    <td>{p.examFeeAmount.toLocaleString()}</td>
                    <td>
                      <ActionBtn
                        onClick={() => {
                          setClassId(p.classId);
                          setTuitionAmount(p.tuitionAmount);
                          setExamAmount(p.examFeeAmount);
                        }}
                      >
                        <Icon icon="mdi:pencil" color="#1a73e8" width="20" />
                      </ActionBtn>
                      <ActionBtn onClick={() => handleDelete(p._id)}>
                        <Icon icon="mdi:trash-can" color="#d32f2f" width="20" />
                      </ActionBtn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </ContentGrid>

      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copy Presets from Previous Term</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will copy all fee presets from the source term and apply them to
            <strong> {session} {term}</strong>.
          </p>
          <FormGroup>
            <label>Source Academic Session</label>
            <select
              value={copyFromSession}
              onChange={(e) => setCopyFromSession(e.target.value)}
            >
              <option value="2023/2024">2023/2024</option>
              <option value="2024/2025">2024/2025</option>
              <option value="2025/2026">2025/2026</option>
            </select>
          </FormGroup>
          <FormGroup style={{ marginTop: 15 }}>
            <label>Source Term</label>
            <select
              value={copyFromTerm}
              onChange={(e) => setCopyFromTerm(e.target.value)}
            >
              <option value="FIRST TERM">FIRST TERM</option>
              <option value="SECOND TERM">SECOND TERM</option>
              <option value="THIRD TERM">THIRD TERM</option>
            </select>
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </BootstrapButton>
          <BootstrapButton
            variant="warning"
            onClick={handleCopyFromPrevious}
            disabled={loading}
          >
            {loading ? "Copying..." : "Copy Presets"}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  margin-bottom: 25px;
  h3 {
    margin: 0;
    color: #333;
  }
  p {
    color: #666;
    margin-top: 5px;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  background: white;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  select {
    padding: 10px;
    border-radius: 6px;
    border: 1px solid #ddd;
    min-width: 200px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 25px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h4 {
    margin-top: 0;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: 500;
    font-size: 14px;
    color: #444;
  }

  input,
  select {
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #1a73e8;
    }
  }
`;

const Button = styled.button`
  background: #1a73e8;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ActionButton = styled(Button)`
  margin-top: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  box-sizing: border-box;
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  margin-right: 10px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  padding: 30px;
  text-align: center;
  color: #777;
  background: #f9f9f9;
  border-radius: 6px;
`;
